import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  CheckCircleIcon,
  CircleDashedIcon,
  InfoIcon,
  LightningIcon,
  LockSimpleIcon,
  PencilSimpleIcon,
  TrashIcon,
  WarningCircleIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TOTAL_STEPS,
  TRACKER_STEPS,
  criteriaChips,
  stageToStep,
  type SearchSessionSummary,
} from '@entities/search-session';
import { Badge, Button, ButtonLink } from '@shared/ui';

import { awaitingDecisions, orderStageView, stageRoute } from '../model/orderStage';
import { formatActivity, totalNegotiations } from '../model/searchStatus';
import { RenameForm } from './RenameForm';
import styles from './Searches.module.css';

interface SearchSessionCardProps {
  summary: SearchSessionSummary;
  onRename: (title: string | null) => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
  pending: boolean;
}

/**
 * Иконка бейджа стадии. **Цвет никогда не единственный носитель смысла**
 * (DESIGN_SYSTEM §2.4): у каждого тона своя иконка, и в оттенках серого
 * карточка остаётся читаемой.
 */
const TONE_ICONS: Record<string, ReactNode> = {
  match: <CheckCircleIcon size={14} weight="bold" />,
  action: <LightningIcon size={14} weight="bold" />,
  info: <InfoIcon size={14} weight="bold" />,
  compromise: <WarningIcon size={14} weight="bold" />,
  attention: <WarningCircleIcon size={14} weight="bold" />,
  neutral: <CircleDashedIcon size={14} weight="bold" />,
};

/**
 * Карточка заказа в списке (FE-200, MVP_PRD §20, §33.2).
 *
 * Показывает ровно то, что посчитал backend: название, **стадию воронки и шаг
 * трекера**, признак оплаты, резюме критериев, дату активности, размер рабочего
 * набора, состав четвёрки, счётчики переговоров и число ожидающих решения.
 * Ничего не выводится «по смыслу»: `null` в агрегате означает «этого ещё не
 * считали», и рисовать вместо него ноль было бы враньём.
 *
 * Три правила, которые нельзя «упростить»:
 *
 * * **шаг считает чистая функция `stageToStep`**, а не карточка и не backend
 *   (FE-192): страница не знает о шагах и не рисует навигацию;
 * * **оплата определяется полем `paid`, а не стадией** — правка критериев
 *   возвращает оплаченный заказ к анализу, но купленную работу не отбирает
 *   (MVP_PRD §35.4);
 * * **ожидание решения пользователя подсвечивается на карточке** одновременно
 *   с трекером (MVP_PRD §33.3) — это то, из-за чего заказ стоит.
 */
export function SearchSessionCard({
  summary,
  onRename,
  onArchive,
  onRestore,
  onDelete,
  pending,
}: SearchSessionCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const [renaming, setRenaming] = useState(false);

  const { session } = summary;
  const view = orderStageView(session.stage);
  const archived = session.status === 'archived';
  const step = stageToStep(session.stage);
  const trackerStep = TRACKER_STEPS.find((item) => item.number === step);
  const chips = summary.criteria
    ? criteriaChips(summary.criteria, (key, params) => t(key, params ?? {}), locale).slice(0, 6)
    : [];
  const negotiations = totalNegotiations(summary.negotiation_counts);
  const awaiting = awaitingDecisions(summary.negotiation_counts);

  // Куда ведёт основное действие. Прогон в адрес не подставляется: у результатов
  // он свой, и страница возьмёт текущий прогон заказа сама.
  const target = stageRoute(session.stage, session.id);

  return (
    <article className={[styles.card, archived && styles.cardArchived].filter(Boolean).join(' ')}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          {renaming ? (
            <RenameForm
              initialTitle={session.title}
              pending={pending}
              onCancel={() => {
                setRenaming(false);
              }}
              onSubmit={(title) => {
                onRename(title);
                setRenaming(false);
              }}
            />
          ) : (
            <h3 className={styles.title}>
              {session.title ?? <span className={styles.untitled}>{t('searches.untitled')}</span>}
            </h3>
          )}

          <p className={styles.meta}>
            <span>
              {t('searches.card.created', {
                value: formatActivity(session.created_at, locale),
              })}
            </span>
            <span>
              {t('searches.card.activity', {
                value: formatActivity(session.last_activity_at, locale),
              })}
            </span>
          </p>
        </div>

        <div className={styles.badges}>
          <Badge tone={view.tone} icon={TONE_ICONS[view.tone]} className={styles.chip}>
            {t(view.labelKey)}
          </Badge>
          {/* Оплата — отдельный факт, а не оттенок стадии: заказ может стоять
              на анализе и быть оплаченным (MVP_PRD §35.4). */}
          {session.paid ? (
            <Badge tone="match" icon={<CheckCircleIcon size={14} weight="bold" />}>
              {t('searches.card.paid')}
            </Badge>
          ) : (
            <Badge tone="neutral" icon={<LockSimpleIcon size={14} weight="fill" />}>
              {t('searches.card.unpaid')}
            </Badge>
          )}
          {archived ? <Badge tone="neutral">{t('searches.stage.archived')}</Badge> : null}
        </div>
      </div>

      {/* Шаг воронки: то же «Schritt 4 von 7 · Bewertung», что и в шапке на
          мобильном. Заказ вне воронки (закрыт, в архиве) шага не имеет, и
          подсвечивать ему чужой было бы враньём о состоянии. */}
      {step !== null && trackerStep ? (
        <p className={styles.step}>
          <span className={styles.stepLabel}>
            {t('app.tracker.compact', {
              number: step,
              total: TOTAL_STEPS,
              name: t(`app.tracker.step.${trackerStep.key}`),
            })}
          </span>
          <span className={styles.stepTrack} aria-hidden="true">
            <span
              className={styles.stepFill}
              style={{ transform: `scaleX(${step / TOTAL_STEPS})` }}
            />
          </span>
        </p>
      ) : null}

      {chips.length > 0 ? (
        <ul className={styles.chips} aria-label={t('searches.card.criteria')}>
          {chips.map((chip) => (
            <li key={chip.key}>
              {/* Обязательное условие обязано отличаться от пожелания
                  визуально и текстом, а не только цветом (MVP_PRD §19).
                  Синий тинт, как у чипов на анализе и в результатах: красный
                  на экране — только главное действие (DESIGN_SYSTEM §2.2).
                  Обычный регистр: чип — значение критерия, а не надзаголовок. */}
              <Badge
                tone={chip.strength === 'required' ? 'action' : 'neutral'}
                uppercase={false}
                className={styles.chip}
              >
                {chip.label}
                {chip.strength === 'required' ? (
                  <span className="visually-hidden"> {t('results.chip.required')}</span>
                ) : null}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}

      <dl className={styles.aggregates}>
        {session.stage === 'draft' && summary.readiness_score !== null ? (
          <div className={styles.aggregate}>
            <dt>{t('searches.card.readiness')}</dt>
            <dd className="tabular">{Math.round(summary.readiness_score)}%</dd>
          </div>
        ) : null}

        {summary.result_count !== null ? (
          <div className={styles.aggregate}>
            <dt>{t('searches.card.results')}</dt>
            <dd className="tabular">{summary.result_count}</dd>
          </div>
        ) : null}

        {summary.best_score !== null ? (
          <div className={styles.aggregate}>
            <dt>{t('searches.card.bestScore')}</dt>
            <dd className="tabular">{Math.round(summary.best_score)}/100</dd>
          </div>
        ) : null}

        {/* Состав четвёрки. `null` — отбор ещё не выполнялся: это не ноль. */}
        {summary.selection_count !== null ? (
          <div className={styles.aggregate}>
            <dt>{t('searches.card.selection')}</dt>
            <dd className="tabular">{summary.selection_count}/4</dd>
          </div>
        ) : null}

        {negotiations > 0 ? (
          <div className={styles.aggregate}>
            <dt>{t('searches.card.negotiations')}</dt>
            <dd className="tabular">{negotiations}</dd>
          </div>
        ) : null}

        {session.replacement_count > 0 ? (
          <div className={styles.aggregate}>
            <dt>{t('searches.card.replacements')}</dt>
            <dd className="tabular">{session.replacement_count}</dd>
          </div>
        ) : null}
      </dl>

      {/* Заказ ждёт пользователя — это главное, что он должен увидеть в списке
          (MVP_PRD §33.3). Не цвет, а строка с иконкой и числом. */}
      {awaiting > 0 ? (
        <p className={styles.awaiting}>
          <WarningCircleIcon size={16} weight="bold" aria-hidden="true" />
          {t('searches.card.awaiting', { count: awaiting })}
        </p>
      ) : null}

      {/* Действия на карточке красными не бывают никогда (DESIGN_SYSTEM §5.1):
          красная кнопка экрана одна — «Bestellung starten». Основное действие
          карточки — синим контуром, остальные — `ghost`. */}
      <div className={styles.actions}>
        {archived ? (
          <Button
            variant="outline-action"
            disabled={pending}
            iconEnd={<ArrowCounterClockwiseIcon size={16} weight="bold" />}
            onClick={onRestore}
          >
            {t('searches.action.restore')}
          </Button>
        ) : (
          <ButtonLink to={target} variant="outline-action">
            {t(view.actionKey)}
          </ButtonLink>
        )}

        <div className={styles.secondaryActions}>
          {archived ? null : (
            <>
              <Button
                variant="ghost"
                disabled={pending}
                iconEnd={<PencilSimpleIcon size={16} />}
                onClick={() => {
                  setRenaming((open) => !open);
                }}
              >
                {t('searches.action.rename')}
              </Button>
              <Button
                variant="ghost"
                disabled={pending}
                iconEnd={<ArchiveIcon size={16} />}
                onClick={onArchive}
              >
                {t('searches.action.archive')}
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            disabled={pending}
            iconEnd={<TrashIcon size={16} />}
            onClick={onDelete}
          >
            {t('searches.action.delete')}
          </Button>
        </div>
      </div>
    </article>
  );
}
